import clsx from 'clsx';
import { Copy, KeyRound, Plus, Search, Sparkles, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { effectivePermissions, HOLIDAY_CALENDARS, PERMISSIONS, PROTECTED_PERMISSIONS, ROLE_PERMISSIONS, ROLES, type Permission, type Role } from '@pz/core';
import { Avatar } from '../components/avatar';
import { HoursInput } from '../components/entry';
import { ScrollBox, ScrollTable, Sentinel, useChunked } from '../components/virtual';
import {
  Button,
  Checkbox,
  ConfirmModal,
  EmptyState,
  ErrorBox,
  Field,
  IconButton,
  Input,
  Loading,
  matchesSearch,
  Modal,
  Notice,
  PageHeader,
  Panel,
  Pill,
  Select,
  SortTh,
  Tabs,
  Textarea,
  td,
  th,
  useToast,
} from '../components/ui';
import { useTableSort } from '../lib/sort';
import { d, money, num, wd } from '../lib/format';
import { useM, useQ, useTabParam } from '../lib/hooks';
import { useMe } from '../lib/me';
import { rpc } from '../lib/rpc';
import { MoneyInput } from './Billing';

const WEEKDAYS = ['2026-09-21', '2026-09-22', '2026-09-23', '2026-09-24', '2026-09-25', '2026-09-26', '2026-09-27'];

function SetupLink({ url }: { url: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  return (
    <Notice tone="good">
      <div className="mb-1 font-medium">{t('people.setupLink')}</div>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-sheet px-2 py-1 text-xs text-ink" title={url}>
          {url}
        </code>
        <IconButton
          label={t('common.copy')}
          icon={<Copy className="size-4" />}
          onClick={() =>
            navigator.clipboard
              .writeText(url)
              .then(() => toast.ok(t('common.copied')))
              .catch(() => {})
          }
        />
      </div>
      <div className="mt-1 text-xs">{t('people.setupLinkHint')}</div>
    </Notice>
  );
}

export function PeoplePage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = useMe();
  const [search, setSearch] = useState('');
  const [inactive, setInactive] = useState(false);
  const [create, setCreate] = useState(false);
  const q = useQ('people.list', { includeInactive: inactive });
  const fill = useM('people.fillCodes', { invalidate: ['people.', 'billing.'] });
  // contract hours: Personal (HR) and management only
  const seesHours = can('hr.manage', 'kpi.team.view');
  const manage = can('users.manage', 'hr.manage');
  const all = useMemo(() => q.data?.people ?? [], [q.data]);
  type PersonRow = (typeof all)[number];
  const sorting = useTableSort<PersonRow>('people', {
    name: (p) => p.displayName,
    code: (p) => p.shortCode,
    role: (p) => ('role' in p ? p.role : null),
    discipline: (p) => p.discipline,
    country: (p) => p.locationCountry,
    hours: (p) => ('weeklyHours' in p ? p.weeklyHours : null),
    entry: (p) => ('entryDate' in p ? p.entryDate : null),
  });
  const list = useMemo(
    () => sorting.apply(all.filter((p) => matchesSearch(`${p.displayName} ${p.shortCode ?? ''} ${p.discipline ?? ''} ${'email' in p ? p.email : ''} ${'company' in p ? (p.company ?? '') : ''}`, search))),
    [all, search, sorting.sort], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const chunk = useChunked(list, 100, `${search}|${inactive}|${sorting.sort?.key}|${sorting.sort?.dir}`);
  // people without a short code: the office can fill them in one step (suggested from the name, unique)
  const missing = manage ? all.filter((p) => p.active && p.kind === 'employee' && !p.shortCode).length : 0;
  if (!manage) return <EmptyState>{t('people.noAccess')}</EmptyState>;
  return (
    <div>
      <PageHeader
        title={t('people.title')}
        subtitle={t('people.subtitle')}
        actions={
          manage && (
            <>
              {missing > 0 && (
                <Button
                  icon={<Sparkles className="size-4" />}
                  loading={fill.isPending}
                  title={t('people.fillCodesHint')}
                  onClick={() =>
                    fill
                      .mutateAsync({})
                      .then((r) => toast.ok(t('people.fillCodesDone', { count: r.filled })))
                      .catch(toast.error)
                  }
                >
                  {t('people.fillCodes', { count: missing })}
                </Button>
              )}
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreate(true)}>
                {t('people.new')}
              </Button>
            </>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-line bg-sheet p-3">
        <div className="relative min-w-64 flex-1">
          <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-faint" />
          <Input className="pl-8" placeholder={t('people.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Checkbox label={t('common.showInactive')} checked={inactive} onChange={(e) => setInactive(e.target.checked)} />
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && list.length === 0 && <EmptyState>{t('common.noResults')}</EmptyState>}
      {list.length > 0 && (
        <>
          <ScrollTable label={t('people.title')}>
            <table className="w-full min-w-[820px] text-sm">
              <thead className="border-b border-line bg-sheet-2">
                <tr>
                  <SortTh label={t('common.name')} sortKey="name" sort={sorting.sort} onSort={sorting.toggle} />
                  <SortTh label={<span title={t('people.shortCodeHint')}>{t('people.shortCode')}</span>} sortKey="code" sort={sorting.sort} onSort={sorting.toggle} />
                  <SortTh label={t('common.role')} sortKey="role" sort={sorting.sort} onSort={sorting.toggle} />
                  <SortTh label={t('people.discipline')} sortKey="discipline" sort={sorting.sort} onSort={sorting.toggle} />
                  <SortTh label={t('people.country')} sortKey="country" sort={sorting.sort} onSort={sorting.toggle} />
                  {seesHours && <SortTh label={t('people.weeklyHours')} sortKey="hours" sort={sorting.sort} onSort={sorting.toggle} right />}
                  <SortTh label={t('people.entryDate')} sortKey="entry" sort={sorting.sort} onSort={sorting.toggle} />
                </tr>
              </thead>
              <tbody>
                {chunk.visible.map((p) => (
                  <tr key={p.id} className={clsx('border-b border-line/70 hover:bg-sheet-2', !p.active && 'text-graphite')}>
                    <td className={td}>
                      <div className="flex items-center gap-2.5">
                        <Avatar personId={p.id} name={p.displayName} avatarAt={p.avatarAt} size={30} />
                        <div className="min-w-0">
                          <Link to={`/people/${p.id}`} className="font-medium hover:text-accent">
                            {p.displayName}
                          </Link>
                          {'email' in p && <div className="truncate text-xs text-graphite">{p.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td className={clsx(td, 'num font-medium')}>{p.shortCode ?? <span className="font-normal text-faint">-</span>}</td>
                    <td className={td}>
                      {'role' in p && <Pill tone={p.role === 'external' ? 'warn' : p.role === 'employee' ? 'neutral' : 'accent'}>{t(`people.roles.${p.role as Role}`)}</Pill>}
                      {!p.active && <Pill className="ml-1">{t('common.inactive')}</Pill>}
                    </td>
                    <td className={td}>{p.discipline}</td>
                    <td className={td}>
                      {p.locationCountry}
                      {'holidayCalendar' in p && p.holidayCalendar !== p.locationCountry && <span className="text-xs text-graphite"> · {p.holidayCalendar}</span>}
                    </td>
                    {seesHours && <td className={clsx(td, 'num text-right')}>{'weeklyHours' in p && p.weeklyHours !== null ? p.weeklyHours : ''}</td>}
                    <td className={clsx(td, 'num')}>{'entryDate' in p ? d(p.entryDate) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {chunk.hasMore && <Sentinel onMore={chunk.more} />}
          </ScrollTable>
          <p className="mt-2 text-xs text-graphite">{chunk.hasMore ? t('entries.showing', { shown: chunk.shown, total: chunk.total }) : t('people.count', { count: chunk.total })}</p>
        </>
      )}
      <CreatePersonModal open={create} onClose={() => setCreate(false)} />
    </div>
  );
}

interface PersonForm {
  email: string;
  displayName: string;
  shortCode: string;
  kind: 'employee' | 'external';
  role: Role;
  language: 'de' | 'en' | 'tr';
  discipline: string;
  company: string;
  locationCountry: string;
  holidayCalendar: string;
  entryDate: string;
  exitDate: string;
  notes: string;
  weeklyHours: number;
  /** job title: shown only, the rights do not depend on it */
  jobTitle: string;
  /** HR: an overtime account is kept (off: no balance, no time off in lieu) */
  overtimeAccount: boolean;
}

const EMPTY: PersonForm = {
  email: '',
  displayName: '',
  shortCode: '',
  kind: 'employee',
  role: 'employee',
  language: 'de',
  discipline: '',
  company: '',
  locationCountry: 'DE',
  holidayCalendar: 'DE-NW',
  entryDate: '',
  exitDate: '',
  notes: '',
  weeklyHours: 40,
  jobTitle: '',
  overtimeAccount: true,
};

/**
 * Accounts with protected rights (see PROTECTED_PERMISSIONS): role, access, e-mail and activation are changed
 * only by someone holding the same rights (the server enforces this too).
 */
function useLockedAccount(p: { id: string; role: string; extraPermissions: string[] }) {
  const { can, personId } = useMe();
  if (p.id === personId) return false;
  const perms = effectivePermissions(p.role as Role, p.extraPermissions);
  return PROTECTED_PERMISSIONS.some((x) => perms.has(x) && !can(x));
}

/** Roles the current user may assign: protected rights only by a holder; the first management account by the admin. */
function useRoleAllowed() {
  const { can } = useMe();
  const list = useQ('people.list', {});
  // the first account of office and management is set up by the admin at the start of the system
  const noOffice = !!list.data && !list.data.people.some((x) => x.role === 'office' && x.active);
  return (r: Role) => ROLE_PERMISSIONS[r].every((x) => !PROTECTED_PERMISSIONS.includes(x) || can(x)) || (r === 'office' && noOffice);
}

function PersonFields({ f, set, disabled, create, emailLocked, personId }: { f: PersonForm; set: (f: PersonForm) => void; disabled?: boolean; create?: boolean; emailLocked?: boolean; personId?: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = useMe();
  const roleAllowed = useRoleAllowed();
  // the code is suggested from the name: initials (HA), then one more letter if they are taken (HAL)
  const suggest = async () => {
    try {
      const r = await rpc('people.suggestCode', { name: f.displayName, exceptId: personId });
      if (r.code) set({ ...f, shortCode: r.code });
    } catch (e) {
      toast.error(e);
    }
  };
  const field = (k: keyof PersonForm) => ({ value: String(f[k] ?? ''), disabled, onChange: (e: { target: { value: string } }) => set({ ...f, [k]: e.target.value }) });
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label={t('people.displayName')}>
        <Input {...field('displayName')} />
      </Field>
      <Field label={t('common.email')}>
        <Input type="email" {...field('email')} disabled={disabled || emailLocked} title={emailLocked ? t('people.protectedHint') : undefined} />
      </Field>
      <Field label={t('people.shortCode')} hint={t('people.shortCodeHint')}>
        <div className="flex gap-1.5">
          <Input
            className="num uppercase"
            value={f.shortCode}
            disabled={disabled}
            maxLength={6}
            placeholder={create ? t('people.shortCodeAuto') : undefined}
            onChange={(e) => set({ ...f, shortCode: e.target.value.replace(/[\s.]/g, '').toUpperCase() })}
          />
          {!disabled && (
            <IconButton label={t('people.suggestCode')} icon={<Sparkles className="size-4" />} disabled={!f.displayName.trim()} onClick={suggest} />
          )}
        </div>
      </Field>
      <Field label={t('people.discipline')}>
        <Input {...field('discipline')} />
      </Field>
      <Field label={t('people.jobTitle')} hint={t('people.jobTitleHint')}>
        <Input {...field('jobTitle')} maxLength={80} />
      </Field>
      {create && (
        <Field label={t('common.role')}>
          <Select
            value={f.role}
            onChange={(e) => {
              const role = e.target.value as Role;
              set({ ...f, role, kind: role === 'external' ? 'external' : 'employee' });
            }}
          >
            {ROLES.filter((r) => (can('users.manage') || r === 'employee' || r === 'external') && roleAllowed(r)).map((r) => (
              <option key={r} value={r}>
                {t(`people.roles.${r}`)}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <Field label={t('profile.language')}>
        <Select {...field('language')}>
          <option value="de">Deutsch</option>
          <option value="en">English</option>
          <option value="tr">Türkçe</option>
        </Select>
      </Field>
      {f.kind === 'external' && (
        <Field label={t('people.company')}>
          <Input {...field('company')} />
        </Field>
      )}
      <Field label={t('people.country')}>
        <Select
          value={f.locationCountry}
          disabled={disabled}
          onChange={(e) =>
            set({
              ...f,
              locationCountry: e.target.value,
              holidayCalendar: e.target.value === 'TR' ? 'TR' : f.holidayCalendar === 'TR' ? 'DE-NW' : f.holidayCalendar,
            })
          }
        >
          <option value="DE">{t('people.countries.DE')}</option>
          <option value="TR">{t('people.countries.TR')}</option>
        </Select>
      </Field>
      <Field label={t('people.calendar')}>
        <Select {...field('holidayCalendar')}>
          {HOLIDAY_CALENDARS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('people.entryDate')}>
        <Input type="date" {...field('entryDate')} />
      </Field>
      <Field label={t('people.exitDate')}>
        <Input type="date" {...field('exitDate')} />
      </Field>
      {create && f.kind === 'employee' && can('hr.manage') && (
        <Field label={t('people.weeklyHours')} hint={t('people.weeklyHoursHint')}>
          <Input type="number" min={0} max={80} value={f.weeklyHours} onChange={(e) => set({ ...f, weeklyHours: Number(e.target.value) })} />
        </Field>
      )}
      {can('hr.manage') && f.kind === 'employee' && (
        <Field label={t('people.overtimeAccount')} hint={t('people.overtimeAccountHint')} className="sm:col-span-2">
          <Checkbox label={t('people.overtimeAccount')} checked={f.overtimeAccount} disabled={disabled} onChange={(e) => set({ ...f, overtimeAccount: e.target.checked })} />
        </Field>
      )}
      {can('hr.manage') && (
        <Field label={t('people.notes')} className="sm:col-span-2">
          <Textarea rows={2} {...field('notes')} />
        </Field>
      )}
    </div>
  );
}

function CreatePersonModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [f, setF] = useState<PersonForm>(EMPTY);
  const [link, setLink] = useState<{ url: string; id: string } | null>(null);
  useEffect(() => {
    if (open) {
      setF(EMPTY);
      setLink(null);
    }
  }, [open]);
  const create = useM('people.create', { invalidate: ['people.'] });
  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={t('people.new')}
      footer={
        link ? (
          <Button variant="primary" onClick={() => (onClose(), navigate(`/people/${link.id}`))}>
            {t('common.open')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!f.email || !f.displayName}
              loading={create.isPending}
              onClick={() =>
                create
                  .mutateAsync({
                    ...f,
                    shortCode: f.shortCode || null,
                    discipline: f.discipline || null,
                    company: f.company || null,
                    holidayCalendar: f.holidayCalendar as 'DE-NW',
                    entryDate: f.entryDate || null,
                    exitDate: f.exitDate || null,
                    notes: f.notes || null,
                    weeklyHours: f.kind === 'employee' ? f.weeklyHours : undefined,
                  })
                  .then((r) => {
                    const url = (r as { setupUrl?: string }).setupUrl ?? `${window.location.origin}${window.location.pathname}#/setup/${r.setupToken}`;
                    setLink({ url, id: r.id });
                  })
                  .catch(() => {})
              }
            >
              {t('common.create')}
            </Button>
          </>
        )
      }
    >
      {link ? <SetupLink url={link.url} /> : <PersonFields f={f} set={setF} create />}
      <div className="mt-3">
        <ErrorBox error={create.error} />
      </div>
    </Modal>
  );
}

type Tab = 'master' | 'time' | 'rates' | 'access';
const PERSON_TABS: readonly Tab[] = ['master', 'time', 'rates', 'access'];

export function PersonPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const q = useQ('people.get', { id });
  const [tab, setTab] = useTabParam<Tab>(PERSON_TABS);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const p = q.data.person;
  const tabs: { id: Tab; label: string; hidden: boolean }[] = [
    { id: 'master', label: t('people.tabs.master'), hidden: false },
    { id: 'time', label: t('people.tabs.time'), hidden: p.kind !== 'employee' || (!q.data.permissions.editHr && q.data.contracts.length === 0) },
    { id: 'rates', label: t('people.tabs.rates'), hidden: !q.data.permissions.editSalesRates && !q.data.permissions.editCostRates && q.data.rates.length === 0 },
    { id: 'access', label: t('people.tabs.access'), hidden: !q.data.permissions.editAccess },
  ];
  const shown: Tab = tabs.some((x) => x.id === tab && !x.hidden) ? tab : 'master';
  return (
    <div>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            <Avatar personId={p.id} name={p.displayName} avatarAt={p.avatarAt} size={36} />
            {p.displayName}
            {p.shortCode && (
              <Pill title={t('people.shortCode')} className="num">
                {p.shortCode}
              </Pill>
            )}
            <Pill tone="accent">{t(`people.roles.${p.role as Role}`)}</Pill>
            {!p.active && <Pill>{t('common.inactive')}</Pill>}
          </span>
        }
        subtitle={[p.email, p.discipline, p.locationCountry].filter(Boolean).join(' · ')}
      />
      <Tabs value={shown} onChange={setTab} tabs={tabs} />
      {shown === 'master' && <MasterTab data={q.data} />}
      {shown === 'time' && <TimeTab data={q.data} />}
      {shown === 'rates' && <RatesTab data={q.data} />}
      {shown === 'access' && <AccessTab data={q.data} />}
    </div>
  );
}

type PersonData = NonNullable<ReturnType<typeof usePerson>['data']>;
function usePerson(id: string) {
  return useQ('people.get', { id });
}

function MasterTab({ data }: { data: PersonData }) {
  const { t } = useTranslation();
  const toast = useToast();
  const p = data.person;
  const initial = useMemo<PersonForm>(
    () => ({
      email: p.email,
      displayName: p.displayName,
      shortCode: p.shortCode ?? '',
      kind: p.kind as 'employee',
      role: p.role as Role,
      language: p.language as 'de',
      discipline: p.discipline ?? '',
      company: p.company ?? '',
      locationCountry: p.locationCountry,
      holidayCalendar: p.holidayCalendar,
      entryDate: p.entryDate ?? '',
      exitDate: p.exitDate ?? '',
      notes: p.notes ?? '',
      weeklyHours: 40,
      jobTitle: p.jobTitle ?? '',
      overtimeAccount: p.overtimeAccount ?? true,
    }),
    [p],
  );
  const [f, setF] = useState(initial);
  useEffect(() => setF(initial), [initial]);
  const update = useM('people.update', { invalidate: ['people.'] });
  const edit = data.permissions.editHr || data.permissions.editAccess;
  const locked = useLockedAccount(p);
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
      <Panel
        title={t('people.tabs.master')}
        actions={
          edit && (
            <Button
              size="sm"
              variant="primary"
              loading={update.isPending}
              onClick={() =>
                update
                  .mutateAsync({
                    id: p.id,
                    email: f.email,
                    displayName: f.displayName,
                    shortCode: f.shortCode || null,
                    language: f.language,
                    discipline: f.discipline || null,
                    company: f.company || null,
                    locationCountry: f.locationCountry,
                    holidayCalendar: f.holidayCalendar as 'DE-NW',
                    entryDate: f.entryDate || null,
                    exitDate: f.exitDate || null,
                    notes: f.notes || null,
                    jobTitle: f.jobTitle.trim() || null,
                    ...(p.overtimeAccount !== null && p.overtimeAccount !== undefined ? { overtimeAccount: f.overtimeAccount } : {}),
                  })
                  .then(() => toast.ok(t('common.saved')))
                  .catch(toast.error)
              }
            >
              {t('common.save')}
            </Button>
          )
        }
      >
        <PersonFields f={f} set={setF} disabled={!edit} emailLocked={locked} personId={p.id} />
      </Panel>
      <div className="flex flex-col gap-4">
      {p.kind === 'employee' && <ApproverPanel data={data} />}
      <ProjectsPanel data={data} />
      {data.absenceSummary && (
        <Panel title={t('people.absenceSummary', { year: data.absenceSummary.year })}>
          {data.absenceSummary.balance.entitlement !== null && (
            <p className="mb-2 text-sm">
              {t('leave.balance.available')}:{' '}
              <span className="num font-semibold">
                {num(data.absenceSummary.balance.available)} {t(`leave.units.${data.absenceSummary.balance.unit}`)}
              </span>
            </p>
          )}
          {Object.keys(data.absenceSummary.days).length === 0 && <p className="text-sm text-graphite">{t('common.noData')}</p>}
          <ul className="flex flex-col gap-1 text-sm">
            {Object.entries(data.absenceSummary.days).map(([k, v]) => (
              <li key={k} className="flex justify-between">
                <span>{t(`week.absence.${k as 'vacation'}`)}</span>
                <span className="num">{t('people.days', { count: v })}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      </div>
    </div>
  );
}

/**
 * Who decides the absence requests of the person: an approver (e.g. the team lead) or, without one, the holders
 * of the right "Personal". Sick reports are always confirmed by "Personal". Set by "Personal".
 */
function ApproverPanel({ data }: { data: PersonData }) {
  const { t } = useTranslation();
  const { can } = useMe();
  const toast = useToast();
  const hr = can('hr.manage');
  const people = useQ('people.list', {}, { enabled: hr });
  const save = useM('people.setApprover', { invalidate: ['people.', 'leave.', 'me.'] });
  const p = data.person;
  const [value, setValue] = useState(p.approver?.id ?? '');
  useEffect(() => setValue(p.approver?.id ?? ''), [p.approver?.id]);
  const candidates = (people.data?.people ?? []).filter((x) => x.active && x.kind === 'employee' && x.id !== p.id);
  return (
    <Panel title={t('people.approver.title')}>
      <p className="mb-2 text-sm text-graphite">{t('people.approver.hint')}</p>
      {hr ? (
        <div className="flex flex-col gap-2">
          <Select value={value} onChange={(e) => setValue(e.target.value)} aria-label={t('people.approver.title')}>
            <option value="">{t('people.approver.hr')}</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            className="self-start"
            disabled={value === (p.approver?.id ?? '')}
            loading={save.isPending}
            onClick={() =>
              save
                .mutateAsync({ personId: p.id, approverId: value || null })
                .then(() => toast.ok(t('common.saved')))
                .catch(toast.error)
            }
          >
            {t('common.save')}
          </Button>
        </div>
      ) : (
        <p className="text-sm font-medium">{p.approver?.name ?? t('people.approver.hr')}</p>
      )}
    </Panel>
  );
}

function TimeTab({ data }: { data: PersonData }) {
  const { t } = useTranslation();
  const toast = useToast();
  const add = useM('people.addContract', { invalidate: ['people.', 'workload.', 'time.week', 'kpi.'] });
  const del = useM('people.deleteContract', { invalidate: ['people.', 'workload.', 'time.week', 'kpi.'] });
  const [validFrom, setValidFrom] = useState('');
  const [hours, setHours] = useState<number | null>(40 * 60);
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5]);
  return (
    <Panel title={t('people.contracts')}>
      <div className="scroll-x mb-4">
        <table className="w-full text-sm">
          <thead className="border-b border-line">
            <tr>
              <th className={th}>{t('common.from')}</th>
              <th className={clsx(th, 'text-right')}>{t('people.weeklyHours')}</th>
              <th className={th}>{t('people.workdays')}</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {data.contracts.map((c) => (
              <tr key={c.id} className="border-b border-line/70">
                <td className={clsx(td, 'num')}>{d(c.validFrom)}</td>
                <td className={clsx(td, 'num text-right')}>{c.weeklyHours}</td>
                <td className={td}>{c.workdays.map((w) => wd(WEEKDAYS[w - 1])).join(', ')}</td>
                <td className={clsx(td, 'text-right')}>
                  {data.permissions.editHr && (
                    <IconButton
                      label={t('common.delete')}
                      icon={<Trash2 className="size-3.5" />}
                      onClick={() =>
                        del
                          .mutateAsync({ id: c.id })
                          .then(() =>
                            toast.undoable(t('common.deleted'), t('common.undo'), () =>
                              add
                                .mutateAsync({ personId: data.person.id, validFrom: c.validFrom, weeklyHours: c.weeklyHours, workdays: c.workdays })
                                .then(() => toast.ok(t('common.undone')))
                                .catch(toast.error),
                            ),
                          )
                          .catch(toast.error)
                      }
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.permissions.editHr && (
        <div className="flex flex-wrap items-end gap-4 rounded-md border border-line bg-sheet-2 p-3">
          <Field label={t('common.from')}>
            <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          </Field>
          <Field label={t('people.weeklyHours')}>
            <HoursInput value={hours} onChange={setHours} />
          </Field>
          <Field label={t('people.workdays')}>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5, 6, 7].map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setDays(days.includes(w) ? days.filter((x) => x !== w) : [...days, w].sort())}
                  className={clsx('h-8 w-9 rounded border text-xs', days.includes(w) ? 'border-accent bg-accent-soft text-accent' : 'border-line text-graphite')}
                >
                  {wd(WEEKDAYS[w - 1])}
                </button>
              ))}
            </div>
          </Field>
          <Button
            icon={<Plus className="size-4" />}
            disabled={!validFrom || hours === null}
            loading={add.isPending}
            onClick={() =>
              add
                .mutateAsync({ personId: data.person.id, validFrom, weeklyHours: (hours ?? 0) / 60, workdays: days })
                .then(() => toast.ok(t('common.saved')))
                .catch(toast.error)
            }
          >
            {t('people.addContract')}
          </Button>
        </div>
      )}
    </Panel>
  );
}

function RatesTab({ data }: { data: PersonData }) {
  const { t } = useTranslation();
  const toast = useToast();
  const projects = useQ('projects.list', {});
  const set = useM('people.setRate', { invalidate: ['people.', 'billing.', 'kpi.'] });
  const del = useM('people.deleteRate', { invalidate: ['people.', 'billing.', 'kpi.'] });
  const [form, setForm] = useState({ kind: (data.permissions.editSalesRates ? 'sales' : 'cost') as 'sales' | 'cost', projectId: '', validFrom: '', cents: null as number | null });
  const canAdd = data.permissions.editSalesRates || data.permissions.editCostRates;
  return (
    <Panel title={t('people.rates')}>
      <div className="scroll-x mb-4">
        <table className="w-full text-sm">
          <thead className="border-b border-line">
            <tr>
              <th className={th}>{t('common.type')}</th>
              <th className={th}>{t('common.project')}</th>
              <th className={th}>{t('common.from')}</th>
              <th className={clsx(th, 'text-right')}>{t('people.perHour')}</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {data.rates.map((r) => (
              <tr key={r.id} className="border-b border-line/70">
                <td className={td}>
                  <Pill tone={r.kind === 'sales' ? 'accent' : 'neutral'}>{t(`people.rateKind.${r.kind as 'sales'}`)}</Pill>
                </td>
                <td className={td}>{r.projectNumber ?? '-'}</td>
                <td className={clsx(td, 'num')}>{d(r.validFrom)}</td>
                <td className={clsx(td, 'num text-right')}>{money(r.centsPerHour)}</td>
                <td className={clsx(td, 'text-right')}>
                  {canAdd && (
                    <IconButton
                      label={t('common.delete')}
                      icon={<Trash2 className="size-3.5" />}
                      onClick={() =>
                        del
                          .mutateAsync({ id: r.id })
                          .then(() =>
                            toast.undoable(t('common.deleted'), t('common.undo'), () =>
                              set
                                .mutateAsync({ personId: data.person.id, kind: r.kind as 'sales' | 'cost', projectId: r.projectId ?? null, validFrom: r.validFrom, centsPerHour: r.centsPerHour })
                                .then(() => toast.ok(t('common.undone')))
                                .catch(toast.error),
                            ),
                          )
                          .catch(toast.error)
                      }
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canAdd && (
        <div className="flex flex-wrap items-end gap-3 rounded-md border border-line bg-sheet-2 p-3">
          <Field label={t('common.type')}>
            <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as 'sales' })}>
              {data.permissions.editSalesRates && <option value="sales">{t('people.rateKind.sales')}</option>}
              {data.permissions.editCostRates && <option value="cost">{t('people.rateKind.cost')}</option>}
            </Select>
          </Field>
          <Field label={t('people.rateProject')}>
            <Select value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })}>
              <option value="">-</option>
              {(projects.data?.projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.number}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('common.from')}>
            <Input type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} />
          </Field>
          <Field label={t('people.perHour')}>
            <MoneyInput className="w-32" value={form.cents} onChange={(c) => setForm({ ...form, cents: c })} />
          </Field>
          <Button
            icon={<Plus className="size-4" />}
            disabled={!form.validFrom || form.cents === null}
            loading={set.isPending}
            onClick={() =>
              set
                .mutateAsync({ personId: data.person.id, kind: form.kind, projectId: form.projectId || null, validFrom: form.validFrom, centsPerHour: form.cents ?? 0 })
                .then(() => toast.ok(t('common.saved')))
                .catch(toast.error)
            }
          >
            {t('common.add')}
          </Button>
        </div>
      )}
    </Panel>
  );
}

/** Default rights of every role at a glance (request 28 of round 4); protected rights are marked. */
function RoleMatrix() {
  const { t } = useTranslation();
  return (
    <details className="mt-4 rounded-md border border-line px-3 py-2 text-sm">
      <summary className="cursor-pointer font-medium select-none">{t('people.roleMatrix.title')}</summary>
      <div className="scroll-x mt-2">
        <table className="w-full min-w-[520px] text-xs">
          <thead>
            <tr className="border-b border-line">
              <th className="py-1 text-left font-medium text-graphite">{t('people.rights')}</th>
              {ROLES.map((r) => (
                <th key={r} className="px-1 py-1 text-center font-medium text-graphite">
                  {t(`people.roles.${r}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERMISSIONS.map((perm) => (
              <tr key={perm} className="border-b border-line/60">
                <td className="py-1">
                  {t(`people.permissions.${perm}`)}
                  {PROTECTED_PERMISSIONS.includes(perm) && <span className="ml-1 text-faint" title={t('people.roleMatrix.protected')}>🔒</span>}
                </td>
                {ROLES.map((r) => (
                  <td key={r} className="px-1 py-1 text-center">
                    {ROLE_PERMISSIONS[r].includes(perm) ? <span aria-label={t('people.rightTags.default')}>✓</span> : <span className="text-faint">–</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-graphite">{t('people.roleMatrix.hint')}</p>
    </details>
  );
}

function AccessTab({ data }: { data: PersonData }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { can, personId } = useMe();
  const p = data.person;
  const self = p.id === personId;
  const [role, setRole] = useState<Role>(p.role as Role);
  // the rights as a whole: defaults of the role pre-set, single defaults removed, others added
  const [rights, setRights] = useState<Set<string>>(() => new Set(effectivePermissions(p.role as Role, p.extraPermissions, p.removedPermissions ?? [])));
  const [link, setLink] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'reset' | 'active' | null>(null);
  const setRoleM = useM('people.setRole', { invalidate: ['people.', 'me.'] });
  const setActive = useM('people.setActive', { invalidate: ['people.'] });
  const reset = useM('people.resetAccess', { invalidate: ['people.'] });
  const removeAvatar = useM('people.removeAvatar', { invalidate: ['people.'] });
  const defaults = new Set<string>(ROLE_PERMISSIONS[role]);
  const locked = useLockedAccount(p);
  const roleAllowed = useRoleAllowed();
  // roles and rights: administration and management only (rights.manage)
  const editRights = can('rights.manage') && !self && !locked;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {locked && (
        <Notice tone="neutral" className="lg:col-span-2">
          {t('people.protectedHint')}
        </Notice>
      )}
      <Panel
        title={t('common.role')}
        actions={
          <Button
            size="sm"
            variant="primary"
            disabled={!editRights}
            loading={setRoleM.isPending}
            onClick={() =>
              setRoleM
                .mutateAsync({ id: p.id, role, rights: [...rights] })
                .then(() => toast.ok(t('common.saved')))
                .catch(toast.error)
            }
          >
            {t('common.save')}
          </Button>
        }
      >
        {self && (
          <Notice tone="neutral" className="mb-3">
            {t('people.ownRightsHint')}
          </Notice>
        )}
        {!self && !can('rights.manage') && (
          <Notice tone="neutral" className="mb-3">
            {t('people.rightsReadOnly')}
          </Notice>
        )}
        <Field label={t('common.role')}>
          <Select
            value={role}
            disabled={!editRights}
            onChange={(e) => {
              const r = e.target.value as Role;
              setRole(r);
              setRights(new Set(ROLE_PERMISSIONS[r]));
            }}
          >
            {ROLES.map((r) => (
              <option key={r} value={r} disabled={r !== p.role && !roleAllowed(r)}>
                {t(`people.roles.${r}`)}
              </option>
            ))}
          </Select>
        </Field>
        <p className="mt-2 text-xs text-graphite">{t('people.rightsHint')}</p>
        {/* request 28 of round 4: what the role can do without any right (employees and externals have no default rights) */}
        <Notice tone="neutral" className="mt-2 text-xs">
          <span className="font-medium">{t(`people.roles.${role}`)}:</span> {t(`people.roleBase.${role}`)}
        </Notice>
        <div className="mt-4 flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[13px] font-medium text-graphite">{t('people.rights')}</span>
            {editRights && (
              <Button size="sm" variant="ghost" onClick={() => setRights(new Set(ROLE_PERMISSIONS[role]))}>
                {t('people.rightsDefault')}
              </Button>
            )}
          </div>
          {PERMISSIONS.map((perm: Permission) => {
            const isDefault = defaults.has(perm);
            const on = rights.has(perm);
            return (
              <div key={perm} className="flex items-center justify-between gap-2">
                <Checkbox
                  label={<span title={t(`people.permissionHints.${perm}`)}>{t(`people.permissions.${perm}`)}</span>}
                  hint={t(`people.permissionHints.${perm}`)}
                  checked={on}
                  disabled={!editRights || (!on && PROTECTED_PERMISSIONS.includes(perm) && !can(perm))}
                  onChange={(e) => {
                    const next = new Set(rights);
                    if (e.target.checked) next.add(perm);
                    else next.delete(perm);
                    setRights(next);
                  }}
                />
                {isDefault && on && <span className="shrink-0 text-xs text-faint">{t('people.rightTags.default')}</span>}
                {isDefault && !on && <Pill tone="warn">{t('people.rightTags.removed')}</Pill>}
                {!isDefault && on && <Pill tone="info">{t('people.rightTags.extra')}</Pill>}
              </div>
            );
          })}
        </div>
        <RoleMatrix />
      </Panel>
      <Panel title={t('people.access.title')}>
        {data.access && (
          <ul className="mb-4 flex flex-col gap-1 text-sm">
            <li>
              {t('people.access.password')}: {data.access.hasPassword ? t('common.yes') : t('common.no')}
            </li>
            <li>
              {t('people.access.totp')}: {data.access.totpEnabled ? t('common.yes') : t('common.no')}
            </li>
            {data.access.setupPending && <li>{t('people.access.pending')}</li>}
            {p.entraLinked && <li>{t('people.access.entra')}</li>}
          </ul>
        )}
        {link && <SetupLink url={link} />}
        <div className="mt-3 flex flex-wrap gap-2">
          <Button icon={<KeyRound className="size-4" />} disabled={locked} onClick={() => setConfirm('reset')}>
            {t('people.resetAccess')}
          </Button>
          <Button variant={p.active ? 'danger' : 'secondary'} disabled={locked} onClick={() => setConfirm('active')}>
            {p.active ? t('people.deactivate') : t('people.activate')}
          </Button>
          {p.avatarAt && can('users.manage') && !self && (
            <Button variant="ghost" icon={<Trash2 className="size-4" />} loading={removeAvatar.isPending} onClick={() => removeAvatar.mutateAsync({ id: p.id }).then(() => toast.ok(t('common.deleted'))).catch(toast.error)}>
              {t('people.removePicture')}
            </Button>
          )}
        </div>
        <p className="mt-3 text-xs text-graphite">{t('people.accessHint')}</p>
      </Panel>
      <ConfirmModal
        open={confirm === 'reset'}
        onClose={() => setConfirm(null)}
        title={t('people.resetAccess')}
        confirmLabel={t('people.resetAccess')}
        loading={reset.isPending}
        onConfirm={() =>
          reset
            .mutateAsync({ id: p.id })
            .then((r) => {
              setLink((r as { setupUrl?: string }).setupUrl ?? `${window.location.origin}${window.location.pathname}#/setup/${r.setupToken}`);
              setConfirm(null);
            })
            .catch(toast.error)
        }
      />
      <ConfirmModal
        open={confirm === 'active'}
        onClose={() => setConfirm(null)}
        title={p.active ? t('people.deactivate') : t('people.activate')}
        confirmLabel={p.active ? t('people.deactivate') : t('people.activate')}
        danger={p.active}
        loading={setActive.isPending}
        onConfirm={() =>
          setActive
            .mutateAsync({ id: p.id, active: !p.active })
            .then(() => setConfirm(null))
            .catch(toast.error)
        }
      />
    </div>
  );
}

/** Projects the person works on (membership with period): a panel of the master data, not a tab of its own. */
function ProjectsPanel({ data }: { data: PersonData }) {
  const { t } = useTranslation();
  if (data.memberships.length === 0) return null;
  return (
    <Panel title={`${t('people.memberships')} (${data.memberships.length})`} bodyClassName="p-0">
      <ScrollBox maxHeight="16rem" label={t('people.memberships')}>
        <ul className="divide-y divide-line/70 text-sm">
          {data.memberships.map((m) => (
            <li key={m.projectId} className={clsx('flex items-center justify-between gap-2 px-4 py-2', m.archived && 'text-graphite')}>
              <Link to={`/projects/${m.projectId}`} className="min-w-0 truncate hover:text-accent" title={`${m.number} ${m.name}`}>
                <span className="num font-medium">{m.number}</span> {m.name}
              </Link>
              <span className="flex shrink-0 items-center gap-2">
                {m.role === 'lead' && <Pill tone="accent">{t('projects.role.lead')}</Pill>}
                <span className="num text-xs text-graphite">
                  {d(m.validFrom)} - {d(m.validTo)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </ScrollBox>
    </Panel>
  );
}
